import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  StudioState,
  TextInput,
} from "@odingard/design-system";
import type { SopStep, SopStepInput } from "@odingard/sdk-client";
import { useAsync } from "../../lib/useAsync";
import { useAuth } from "../../lib/auth";
import { sopApi } from "../../lib/api";
import { GovernedAction } from "../../governance/GovernedAction";

interface DraftStep extends SopStepInput {
  key: string;
}

let nextKey = 0;
function draftFrom(step: SopStep): DraftStep {
  nextKey += 1;
  return {
    key: `step-${String(nextKey)}`,
    stepOrder: step.stepOrder,
    instruction: step.instruction,
    expectedInputs: step.expectedInputs ?? "",
    isBlocking: step.isBlocking ?? true,
    requiresEvidence: step.requiresEvidence ?? false,
    requiresVerification: step.requiresVerification ?? false,
  };
}

function blankStep(order: number): DraftStep {
  nextKey += 1;
  return {
    key: `step-${String(nextKey)}`,
    stepOrder: order,
    instruction: "",
    expectedInputs: "",
    isBlocking: true,
    requiresEvidence: false,
    requiresVerification: false,
  };
}

export function SopVersionPage() {
  const { activeOrg } = useAuth();
  const navigate = useNavigate();
  const orgId = activeOrg?.id ?? "";
  const { procedureId = "", versionId = "" } = useParams();
  const [steps, setSteps] = useState<DraftStep[]>([]);
  const [subjectType, setSubjectType] = useState("");
  const [subjectReference, setSubjectReference] = useState("");
  const state = useAsync(
    async () => (await sopApi.version(orgId, procedureId, versionId)).data.version,
    [orgId, procedureId, versionId],
  );
  const version = state.data;

  useEffect(() => {
    if (version) setSteps((version.steps ?? []).map(draftFrom));
  }, [version]);

  if (state.loading) return <StudioState state="loading" description="Loading the version…" />;
  if (state.error)
    return <StudioState state="error" description={state.error.message} onRetry={state.reload} />;
  if (!version) return null;

  const editable = version.state === "DRAFT";
  const published = version.state === "PUBLISHED";
  const update = (key: string, patch: Partial<DraftStep>) =>
    setSteps((current) => current.map((step) => (step.key === key ? { ...step, ...patch } : step)));
  const remove = (key: string) =>
    setSteps((current) =>
      current.filter((step) => step.key !== key).map((step, index) => ({ ...step, stepOrder: index + 1 })),
    );
  const move = (index: number, delta: number) =>
    setSteps((current) => {
      const next = [...current];
      const target = index + delta;
      if (target < 0 || target >= next.length) return current;
      [next[index]!, next[target]!] = [next[target]!, next[index]!];
      return next.map((step, position) => ({ ...step, stepOrder: position + 1 }));
    });
  const authored = steps.length > 0 && steps.every((step) => step.instruction.trim().length > 0);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Version {version.versionNumber}</h1>
          <p>{version.changeReason ?? "No change reason recorded."}</p>
        </div>
        <div className="ods-actions">
          <Badge tone={published ? "success" : "neutral"}>{version.state}</Badge>
          {editable && (
            <GovernedAction
              permission="procedures.author"
              label="Submit for approval"
              title="Submit this version for approval"
              description="An approver decides whether this version becomes the way the work is done."
              confirmLabel="Submit"
              requireRationale
              rationaleLabel="Reason"
              onConfirm={(reason) =>
                sopApi.submitVersion(orgId, procedureId, version.id, reason, version.version)
              }
              onDone={state.reload}
            />
          )}
          {version.state === "IN_REVIEW" && (
            <>
              <GovernedAction
                permission="procedures.approve"
                label="Approve"
                title="Approve this version"
                confirmLabel="Approve"
                requireRationale
                rationaleLabel="Reason"
                onConfirm={(reason) =>
                  sopApi.approveVersion(orgId, procedureId, version.id, reason, version.version)
                }
                onDone={state.reload}
              />
              <GovernedAction
                permission="procedures.approve"
                label="Return for changes"
                title="Return this version to its author"
                confirmLabel="Return"
                requireRationale
                rationaleLabel="What has to change"
                onConfirm={(reason) =>
                  sopApi.returnVersionForChanges(orgId, procedureId, version.id, reason, version.version)
                }
                onDone={state.reload}
              />
            </>
          )}
          {version.state === "APPROVED" && (
            <GovernedAction
              permission="procedures.approve"
              label="Publish"
              title="Publish this version"
              consequence="A published version is immutable and becomes the version runs are performed against."
              confirmLabel="Publish"
              requireRationale
              rationaleLabel="Reason"
              onConfirm={(reason) =>
                sopApi.publishVersion(orgId, procedureId, version.id, reason, version.version)
              }
              onDone={state.reload}
            />
          )}
          {published && (
            <GovernedAction
              permission="procedures.execute"
              label="Start a run"
              title="Start a run of this procedure"
              description="A run records what was actually done, step by step, against this published version."
              confirmLabel="Start run"
              triggerVariant="primary"
              onConfirm={async () => {
                const started = await sopApi.startRun(orgId, {
                  versionId: version.id,
                  ...(subjectType.trim() ? { subjectType: subjectType.trim() } : {}),
                  ...(subjectReference.trim() ? { subjectReference: subjectReference.trim() } : {}),
                });
                navigate(`/studio/sop-ai/runs/${started.data.run.id}`);
                return started;
              }}
            >
              <Field label="Subject type" help="What this run is about, for example vendor or contract.">
                {(id) => (
                  <TextInput
                    id={id}
                    value={subjectType}
                    onChange={(event) => setSubjectType(event.target.value)}
                    placeholder="vendor"
                  />
                )}
              </Field>
              <Field label="Subject reference">
                {(id) => (
                  <TextInput
                    id={id}
                    value={subjectReference}
                    onChange={(event) => setSubjectReference(event.target.value)}
                    placeholder="vendor-1"
                  />
                )}
              </Field>
            </GovernedAction>
          )}
        </div>
      </div>
      <Card>
        <CardHeader
          title="Steps"
          action={
            editable ? (
              <div className="ods-actions">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setSteps((current) => [...current, blankStep(current.length + 1)])}
                >
                  Add a step
                </Button>
                <GovernedAction
                  permission="procedures.author"
                  label="Save steps"
                  title="Save the steps of this draft"
                  description="The saved steps replace the draft's current steps."
                  confirmLabel="Save steps"
                  triggerVariant="primary"
                  onConfirm={() =>
                    sopApi.replaceSteps(
                      orgId,
                      procedureId,
                      version.id,
                      steps.map(({ key: _key, ...step }) => step),
                      version.version,
                    )
                  }
                  onDone={state.reload}
                >
                  {!authored && <p className="ods-denial">Every step needs an instruction.</p>}
                </GovernedAction>
              </div>
            ) : undefined
          }
        />
        <CardBody>
          {steps.length === 0 ? (
            <StudioState
              state="empty"
              title="No steps yet"
              description={
                editable
                  ? "Add the steps a person has to perform, in the order they perform them."
                  : "This version declares no steps."
              }
            />
          ) : (
            <ol className="ods-stack">
              {steps.map((step, index) => (
                <li key={step.key} className="ods-card ods-card--inset">
                  {editable ? (
                    <div className="ods-stack">
                      <Field label={`Step ${String(step.stepOrder)} instruction`}>
                        {(id) => (
                          <TextInput
                            id={id}
                            value={step.instruction}
                            onChange={(event) => update(step.key, { instruction: event.target.value })}
                          />
                        )}
                      </Field>
                      <Field label="Expected inputs">
                        {(id) => (
                          <TextInput
                            id={id}
                            value={step.expectedInputs ?? ""}
                            onChange={(event) => update(step.key, { expectedInputs: event.target.value })}
                          />
                        )}
                      </Field>
                      <div className="ods-actions">
                        <label>
                          <input
                            type="checkbox"
                            checked={step.isBlocking ?? true}
                            onChange={(event) => update(step.key, { isBlocking: event.target.checked })}
                          />{" "}
                          Blocking
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            checked={step.requiresEvidence ?? false}
                            onChange={(event) => update(step.key, { requiresEvidence: event.target.checked })}
                          />{" "}
                          Requires evidence
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            checked={step.requiresVerification ?? false}
                            onChange={(event) =>
                              update(step.key, { requiresVerification: event.target.checked })
                            }
                          />{" "}
                          Requires a second person
                        </label>
                        <Button variant="ghost" size="sm" onClick={() => move(index, -1)}>
                          Move up
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => move(index, 1)}>
                          Move down
                        </Button>
                        <Button variant="danger" size="sm" onClick={() => remove(step.key)}>
                          Remove
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <strong>
                        {step.stepOrder}. {step.instruction}
                      </strong>
                      <p>{step.expectedInputs?.trim() ? step.expectedInputs : "No expected inputs."}</p>
                      <div className="ods-actions">
                        {step.requiresEvidence && <Badge tone="info">Evidence required</Badge>}
                        {step.requiresVerification && <Badge tone="info">Second person required</Badge>}
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ol>
          )}
        </CardBody>
      </Card>
    </>
  );
}
